<template>
  <div class="rates-allotment-calendar">
    <FullCalendar
      :period="calendarPeriod"
      :attributes="calendarAttributes"
      :compress="compressCalendarHeight"
    >
      <template #day-content="{ day, attributes }">
        <!-- LOADER -->
        <div v-if="loading" class="custom-calendar--day">
          <div class="custom-calendar--day-content-wrapper opened">
            <div class="date-header">
              <span class="day-label">{{ day.day }}</span>
            </div>

            <div class="date-content">
              <div class="mb-1 w-100 text-center">
                <Skeleton width="40%" height="12px" />
              </div>

              <div class="w-100 text-center">
                <Skeleton width="40%" />
              </div>

              <div
                v-if="!compressCalendarHeight"
                class="text-center disponibility disponibility__loader"
              >
                <Skeleton width="70%" height="12px" />
              </div>
            </div>

            <div v-if="!compressCalendarHeight" class="date-bottom">
              <div class="min-nights">
                <Skeleton width="80%" height="12px" />
              </div>
            </div>
          </div>
        </div>

        <!-- DADOS -->
        <div
          v-else
          :class="{
            'custom-calendar--day__disabled':
              !attributes || attributes.length === 0
          }"
          class="custom-calendar--day"
        >
          <div
            v-if="!attributes || attributes.length === 0"
            class="date-header"
          >
            <span class="day-label">{{ day.day }}</span>
          </div>

          <div
            v-for="(attribute, i) in attributes"
            :key="`day-attr-${i}`"
            :class="{
              opened: attribute.customData.closed === 0,
              closed: attribute.customData.closed === 1
            }"
            class="custom-calendar--day-content-wrapper"
            @mouseenter="
              attribute.customData.showDetail = true
              bindMouseMove($event, attribute)
            "
            @mouseleave="
              attribute.customData.showDetail = false
              unbindMouseMove($event)
            "
            @click="mouseClick(day.date, attribute)"
          >
            <div class="date-header">
              <div
                v-if="
                  disponibilityLabel(attribute.customData).type !== 'resume'
                "
              >
                <span class="disponibility-flag">
                  <span class="day-label">{{ day.day }}</span>
                </span>
              </div>

              <!-- DIFERENCIAL + PREÇO / CALENDARIO COMPRIMIDO -->
              <div
                :class="{
                  price__compressed: compressCalendarHeight,
                  price__normal: !compressCalendarHeight
                }"
              >
                <!-- QUANTIDADE DE HÓSPEDES -->
                <Paragraph
                  v-if="
                    disponibilityLabel(attribute.customData).type !== 'resume'
                  "
                  fontSize="small"
                  class="compre-guest"
                >
                  {{ attribute.customData.guest }}
                </Paragraph>

                <!-- DIFERENCIAL -->
                <Paragraph
                  v-if="attribute.customData.detail"
                  fontSize="x-small"
                  fontWeight="light"
                  class="detail"
                >
                  <span
                    :class="{
                      detail__positive: +attribute.customData.detail.value > 0,
                      detail__negative: +attribute.customData.detail.value < 0
                    }"
                    >{{
                      attribute.customData.detail.value > 0
                        ? `+${attribute.customData.detail.value}`
                        : attribute.customData.detail.value
                    }}{{ attribute.customData.detail.type }}</span
                  >
                </Paragraph>

                <!-- PREÇO -->
                <Paragraph
                  v-if="
                    disponibilityLabel(attribute.customData).type !==
                      'resume' && attribute.customData.price
                  "
                  fontSize="small"
                  fontWeight="bold"
                  class="price"
                  >{{ attribute.customData.price }}
                </Paragraph>
              </div>
            </div>

            <!-- CALENDÁRIO EXPANDIDO -->
            <div class="date-content">
              <!-- PORCENTAGEM DE DESCONTO -->
              <p v-if="attribute.customData.discount" class="discount">
                <span class="value">-{{ attribute.customData.discount }}%</span>
                de desconto
              </p>

              <!-- MINIMO DE NOITES -->
              <div
                class="min-nights"
                v-if="minNoitesLabel(attribute.customData)"
              >
                {{ minNoitesLabel(attribute.customData) }}
              </div>

              <!-- DISPONIBILIDADE -->
              <Paragraph
                v-if="
                  disponibilityLabel(attribute.customData).type !== 'resume' &&
                    attribute.customData.priority
                "
                fontSize="small"
                fontWeight="light"
                class="priority"
              >
                Prioridade ativada
              </Paragraph>

              <!-- CIRCULO - TODOS OS QUARTOS -->
              <div class="date-content">
                <div
                  v-if="attribute.customData.type === 'resume'"
                  class="circle"
                >
                  <div
                    :class="{
                      'circle--green circle--text':
                        attribute.customData.status === 3,
                      'circle--orange circle--text':
                        attribute.customData.status === 2,
                      'circle--red circle--text':
                        attribute.customData.status === 1
                    }"
                  >
                    {{ day.day }}
                  </div>
                </div>
              </div>
            </div>

            <!-- CIRCULO - TODOS OS QUARTOS -->
            <div class="date-bottom">
              <Paragraph
                v-if="attribute.customData.closed !== 1"
                fontSize="x-small"
                class="circle--description"
              >
                {{ disponibilityLabel(attribute.customData).label }}
              </Paragraph>

              <!-- DESCRÇÃO PARA ITEM FECHADO -->
              <Paragraph
                v-if="attribute.customData.closed === 1"
                fontSize="x-small"
                class="closedSales"
              >
                Fechado para vendas
              </Paragraph>
            </div>
          </div>
        </div>
      </template>
    </FullCalendar>

    <!-- TOOLTIP DO HOVER -->
    <DayDetailsTooltip
      :value="
        dayCurrentTargetContent
          ? dayCurrentTargetContent.customData.showDetail
          : false
      "
      :attributes="dayCurrentTargetContent"
      :top="currentDescTooltipPosition.y"
      :left="currentDescTooltipPosition.x"
      class="custom-details-tooltip"
    ></DayDetailsTooltip>
  </div>
</template>

<script>
/**===============================================
 * Componente do calendário de tarifas e allotment
 * =============================================== */
import FullCalendar from '@/components/hotel/rates/FullCalendar'
import DayDetailsTooltip from '@/components/hotel/allotmentAndRatesCalendar/DayDetailsTooltip'
import Skeleton from '@/components/_base/elements/Skeleton'
import Paragraph from '@/components/_base/elements/Paragraph'

export default {
  name: 'RatesAndAllotmentCalendar',
  components: {
    FullCalendar,
    DayDetailsTooltip,
    Skeleton,
    Paragraph
  },
  props: {
    /**
     * Período mostrado no calendário
     *
     * @example { rawStart: Datetime, start: String, rawEnd: Datetime, end: String }
     */
    calendarPeriod: {
      type: Object
    },
    /** Atributos que populam o calendário */
    calendarAttributes: {
      type: Array,
      default: () => []
    },
    /**
     * Exibe a versão comprimida do calendário.
     * @see https://www.figma.com/file/9AFe6i7bVhMup1bYeppCU7/Tarifas?node-id=1%3A8572
     */
    compressCalendarHeight: {
      type: Boolean,
      default: true
    },
    /** Mostra skeleton loader */
    loading: {
      type: Boolean,
      default: false
    },
    /**
     * Tipo de disponibilidade.
     * Valores possíveis: 'letsbook' | 'fechado-in-out' | 'vendido'
     */
    disponibility: {
      type: String,
      validator: function(value) {
        /** O valor deve ser um dos possíveis */
        return ['letsbook', 'fechado-in-out', 'vendido'].indexOf(value) > -1
      }
    },
    /** Apresenta o mínimo de noites */
    minNights: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      /** Posição da tooltip visível de detalhes */
      currentDescTooltipPosition: {
        x: 0,
        y: 0
      },
      /** Coordenadas do mouse */
      mouseCoords: {
        x: 0,
        y: 0
      },
      dayCurrentTarget: null /** Dia que está com o cursor do mouse */,
      dayCurrentTargetContent: null /** Conteúdo do dia que está com o cursor do mouse */,
      dayCurrentTooltipTarget: null /** Tooltip visível ??? Talvez possa ser removido */,
      currentAnimationFrame: null /** Animação atual */
    }
  },
  computed: {
    /**
     * Retorna o label e classe, quando necessário, de disponibilidade
     * @param {Object} dayData Objeto com os valores da diária
     *
     * @returns {Object} - Objeto contendo a label e a classe do resultado
     */
    disponibilityLabel() {
      return (dayData) => {
        let result = {
          label: `Dispon. geral: ${dayData.disponibility}`,
          generalLabel: `Dispon. geral: ${dayData.disponibility}`,
          color: `${
            dayData.disponibility === 0 ? 'disponibility__closed' : ''
          }`,
          type: dayData.type,
          quantity: dayData.disponibility
        }

        switch (this.disponibility) {
          case 'fechado-in-out':
            if (dayData.cta && !dayData.ctd) {
              result = {
                label: 'Fechado para check in',
                color: 'disponibility__cta'
              }
            }

            if (!dayData.cta && dayData.ctd) {
              result = {
                label: 'Fechado para check out',
                color: 'disponibility__ctd'
              }
            }

            if (!dayData.cta && !dayData.ctd) {
              result = {
                label: null,
                color: null
              }
            }

            if ((dayData.cta && dayData.ctd) || dayData.closed > 0) {
              result = {
                label: 'Fechado para check in/out',
                color: 'disponibility__closed'
              }
            }

            break

          case 'vendido':
            result = {
              label: `Vendido: ${dayData.usedQuantity}`
            }
            break
        }

        return result
      }
    },
    /**
     * Retorna o label e classe, quando necessário, de mínimo de noites
     * @param {Object} dayData Objeto com os valores da diária
     *
     * @returns {Object} - Objeto contendo a label e a classe do resultado
     */
    minNoitesLabel() {
      return (dayData) => {
        let result = null

        if (this.minNights && dayData.minNights > 1) {
          if (dayData.minNightsType === 2) {
            result = `${dayData.minNights} noites mín. | pass.`
          } else {
            result = `${dayData.minNights} noites mín. | checkin`
          }
        }

        return result
      }
    }
  },

  methods: {
    /** Verifica se ao clicar deve ser aberto ou não o modal de edição
     * a edição só ocorrera no caso de click em um dia de um quarto selecionado
     */
    async mouseClick(day, attributes) {
      if (attributes.customData.type === 'resume') {
        return 0
      } else {
        this.$emit('showDayEdition', day, attributes.customData)
      }
    },
    /**
     * Inicializa o evento de mousemove e
     * seta os parâmetros iniciais e inicializa
     * a atualização dos frames para a animação
     */
    async bindMouseMove($event, attributes) {
      this.dayCurrentTarget = $event.currentTarget
      this.dayCurrentTargetContent = attributes

      this.dayCurrentTooltipTarget = document.querySelector(
        '.custom-details-tooltip'
      )

      this.dayCurrentTarget.addEventListener(
        'mousemove',
        this.updateMouseCoords
      )

      await this.$nextTick()
      this.seekMousePointer()
    },
    /**
     * Destroy o evento de mouse e cancela a
     * animação corrente
     */
    unbindMouseMove() {
      this.dayCurrentTarget.removeEventListener(
        'mousemove',
        this.updateMouseCoords
      )

      if (this.currentAnimationFrame)
        cancelAnimationFrame(this.currentAnimationFrame)
    },
    /** Atualiza as coordenadas da posição do mouse */
    updateMouseCoords(e) {
      this.mouseCoords.x = e.clientX
      this.mouseCoords.y = e.clientY
    },
    /**
     * Calcula a posição do da tooltip de acordo
     * com a posição do ponteiro do mouse
     */
    seekMousePointer() {
      /** Parâmetros dos limites do elemento */
      const tooltipRectParams = this.dayCurrentTooltipTarget.getBoundingClientRect()

      /** Velocidade que a tooltip se desloca */
      const speed = 0.08

      /** Espaço entre o cursor e a tooltip */
      const gutter = 10

      /** Distância X da tooltip para o cursor */
      let distX = this.mouseCoords.x - tooltipRectParams.left + gutter

      /** Distância Y da tooltip para o cursor */
      let distY = this.mouseCoords.y - tooltipRectParams.top + gutter

      /** Verifica se a posição futura vai cortar da viewport */
      const cut = this.willCutOfViewport(this.dayCurrentTooltipTarget, {
        distX,
        distY
      })

      /** Se cortou na direita, inverte */
      if (cut.right) {
        distX =
          this.mouseCoords.x -
          (tooltipRectParams.left + tooltipRectParams.width) -
          gutter
      }

      /** Se cortou embaixo, inverte */
      if (cut.bottom) {
        distY =
          this.mouseCoords.y -
          (tooltipRectParams.top + tooltipRectParams.height) -
          gutter
      }

      /** Se a tooltip está a uma distância superior a 200px */
      if (Math.abs(distX) > 200 || Math.abs(distY) > 200) {
        /** Move direto, sem animar */
        this.currentDescTooltipPosition.x += distX
        this.currentDescTooltipPosition.y += distY
      } else {
        /** Anima atrás do ponteiro do mouse */
        this.currentDescTooltipPosition.x += distX * speed
        this.currentDescTooltipPosition.y += distY * speed
      }

      this.currentAnimationFrame = requestAnimationFrame(this.seekMousePointer)
    },
    /**
     * Verifica se o elemento vai ser cortado da viewport
     *
     * @param {HTMLElement} elm - Elemento html
     * @param {Object} dist - Distância da tooltip para a nova posição do cursor
     *
     * @example dist {distX: Number, distY: Number}
     */
    willCutOfViewport(elm, dist) {
      const rect = elm.getBoundingClientRect()

      return {
        right: dist.distX + rect.left + rect.width > document.body.clientWidth,
        bottom: dist.distY + rect.top + rect.height > window.innerHeight
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.rates-allotment-calendar {
  /** CALENDÁRIO */
  .priority {
    color: pmw-colors(blue-100);
  }

  .min-nights {
    padding: 4px 9px;
    text-align: center;
    background-color: pmw-colors(grey-10);
  }

  .closedSales {
    text-align: center;
    color: pmw-colors(error-100);
  }

  .guest {
    font-weight: font-weight('weight-medium');
  }

  .detail {
    &__weight-medium {
      font-weight: font-weight('weight-medium');
    }

    &__positive {
      color: $green-secondary;
    }

    &__negative {
      color: $error;
    }
  }

  .disponibility {
    border-radius: 10px;
    box-sizing: content-box;
    font-size: font-size('size-x-small');
    font-weight: font-weight('weight-medium');

    &__closed {
      color: $error;
      padding: {
        left: 2px;
        right: 2px;
      }
    }

    &__cta {
      color: $orange-2;
    }

    &__ctd {
      color: $secondary;
    }

    &__no-disponibility {
      opacity: 0.5;
    }
  }

  .min-nights {
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: font-weight('weight-light');
    font-size: font-size('size-small');
    background-color: inherit;
    margin-bottom: 5px;
  }

  .custom-calendar {
    /** CALENDARIO COMPRIMIDO */
    &__compressed {
      .date-header {
        display: grid;
        grid-template-columns: 30px 1fr;
        align-items: flex-end;

        .price {
          &__normal {
            display: grid;
            justify-content: flex-end;
            padding-right: 10px;

            .compre-guest {
              color: pmw-colors(grey-40);
              text-align: end;
              margin-bottom: 8px;
            }

            .detail {
              margin-right: 4px;
            }
          }
        }

        .price {
          &__compressed {
            display: grid;
            justify-content: flex-end;
            padding-right: 10px;

            .compre-guest {
              color: pmw-colors(grey-40);
              text-align: end;
              margin-bottom: 8px;
            }

            .detail {
              margin-right: 4px;
            }
          }
        }
      }
    } /** FIM DO CALENDARIO COMPRIMIDO */

    /** CALENDARIO NORMAL */
    &__normal {
      .date-header {
        display: grid;
        grid-template-columns: 30px 1fr;
        align-items: flex-end;

        .price {
          &__normal {
            display: grid;
            justify-content: flex-end;
            padding-right: 10px;

            .compre-guest {
              color: pmw-colors(grey-40);
              text-align: end;
              margin-bottom: 8px;
            }

            .detail {
              margin-right: 4px;
            }
          }
        }

        .price {
          &__compressed {
            display: grid;
            justify-content: flex-end;
            padding-right: 10px;

            .compre-guest {
              color: pmw-colors(grey-40);
              text-align: end;
              margin-bottom: 8px;
            }

            .detail {
              margin-right: 4px;
            }
          }
        }
      }
    } /** FIM DO CALENDARIO NORMAL */

    &--day__disabled {
      .date-header {
        .day-label {
          margin-top: 4px;
        }
      }
    }
  }

  ::v-deep {
    .custom-calendar {
      &.vc-container {
        .vc-day,
        .vc-weekday {
          width: calc(988px / 7); /** Tamanho dos dias da semana */
        }

        .vc-day {
          height: 131px;
          z-index: initial;

          &:hover {
            z-index: 2;
          }
        }
      }

      &--day-content-wrapper {
        grid-template-rows: 20px 1fr 20px;
        align-items: center;
        background-color: white;

        &.closed {
          background-color: pmw-colors(grey-10);
        }
        &.opened {
          background-color: $white;
        }
      }

      &--day {
        cursor: pointer;
        @include box-shadow(0px 0 0 transparent);
        transition: box-shadow $anim-fast linear;
        will-change: box-shadow;

        .date-content {
          p + p {
            margin-top: 6px;
          }

          .disponibility {
            width: auto;
            margin: {
              top: 10px;
              bottom: 0px;
            }

            &__loader {
              width: 80%;
            }
          }
        }

        &:hover {
          @include box-shadow(0px 2px 15px rgba(0, 0, 0, 0.15));
        }

        &__disabled {
          cursor: default;

          &:hover {
            box-shadow: none;
          }
        }
      }

      /** CALENDÁRIO NORMAL */
      &__normal {
        &.vc-container {
          .vc-day {
            height: 135px;

            .guest {
              display: none;
            }

            .disponibility {
              margin: 0;
            }
          }
        }
      }
      /** FIM CALENDÁRIO NORMAL */

      /** CALENDÁRIO COMPRIMIDO */
      &__compressed {
        &.vc-container {
          .vc-day {
            height: 120px;

            .guest {
              display: none;
            }

            .disponibility {
              margin: 0;
            }
          }
        }
      }
      /** FIM CALENDÁRIO COMPRIMIDO */
    }
  }
}
.circle {
  width: 50px;
  height: 50px;
  text-align: center;
  padding: 0px 0px 20px 0px;
  margin-bottom: 15px;
  background-color: white;
  background: white;

  &--green {
    border-radius: 50%;
    border: 5px solid pmw-colors(success-100);
  }
  &--red {
    border-radius: 50%;
    border: 5px solid pmw-colors(red-100);
  }
  &--orange {
    border-radius: 50%;
    border: 5px solid pmw-colors(orange-100);
  }

  &--text {
    padding: 9px 0px 12px 0px;
    font-size: 20px;
    font-weight: font-weight('weight-bold');
    color: pmw-colors(grey-100);
  }

  &--description {
    font-size: font-size('size-x-small');
    font-weight: font-weight('weight-medium');
    text-align: center;
    line-height: 20px;
    width: auto;
    background-color: pmw-colors(grey-10);
  }
}
</style>
